/**
 * Outlook Service: frontend wrapper around the per-user Outlook proxy
 * (src/supabase/functions/server/routes/outlook_mail_routes.tsx). It answers
 * in the same shapes as GmailService so the Email view can use either
 * through MailService; Microsoft tokens never reach the browser.
 */
import { SUPABASE_URL } from '../utils/supabase/url';
import { getAuthHeaders } from '../utils/supabase/client';
import { NativeBridge } from './NativeBridge';
import type { GmailLabel, GmailListResponse, GmailMessage, GmailProfile, GmailThread } from './gmailMessage';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/outlook`;

export interface OutlookStatus {
  /** False until the server has the Entra app's secrets. */
  configured: boolean;
  connected: boolean;
  email?: string;
  displayName?: string | null;
  connectedAt?: string;
  /** Microsoft refused the stored sign-in; the user has to link again. */
  needsReconnect?: boolean;
  lastError?: string | null;
  /** The first full pass over the Inbox has finished. */
  inboxIndexed?: boolean;
  syncedAt?: string | null;
}

async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const headers = await getAuthHeaders(method === 'POST' ? { 'Content-Type': 'application/json' } : undefined);
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    ...(method === 'POST' ? { body: JSON.stringify(body ?? {}) } : {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || data.success === false) {
    throw new Error(data.error || `Outlook request failed (${r.status})`);
  }
  return data;
}

const get = <T>(path: string) => request<T>('GET', path);
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body);

export const OutlookService = {
  status(): Promise<OutlookStatus> {
    return get<OutlookStatus & { success: true }>('/status');
  },

  /**
   * Opens Microsoft's sign-in in a popup (or the native auth session). The
   * caller polls status() until it reports connected, as with Google.
   */
  async connect(): Promise<Window | null> {
    if (NativeBridge.isRunningNatively()) {
      const { authUrl } = await post<{ authUrl: string }>('/connect');
      NativeBridge.startAuthSession(authUrl, { title: 'Microsoft 365' });
      return null;
    }
    // Open before awaiting: Safari blocks a popup opened after the click's task has ended.
    const width = 520;
    const height = 680;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open('', 'Microsoft 365', `width=${width},height=${height},left=${left},top=${top}`);
    if (!popup) throw new Error('Allow pop-ups for Quantis, then try again.');
    try {
      const { authUrl } = await post<{ authUrl: string }>('/connect');
      popup.location.href = authUrl;
      return popup;
    } catch (error) {
      popup.close();
      throw error;
    }
  },

  disconnect(): Promise<{ success: true }> {
    return post('/disconnect');
  },
};

/** The mailbox itself, in GmailService's shapes. */
export const OutlookMailService = {
  listMessages(opts: { label?: string; q?: string; pageToken?: string; maxResults?: number } = {}): Promise<GmailListResponse & { success: true }> {
    const params = new URLSearchParams();
    if (opts.label) params.set('label', opts.label);
    if (opts.q) params.set('q', opts.q);
    if (opts.pageToken) params.set('pageToken', opts.pageToken);
    if (opts.maxResults) params.set('maxResults', String(opts.maxResults));
    return get(`/messages?${params}`);
  },

  batchMetadata(ids: string[]): Promise<{ success: true; messages: GmailMessage[] }> {
    return post('/messages/batchMetadata', { ids });
  },

  getMessage(id: string): Promise<{ success: true; message: GmailMessage }> {
    return get(`/messages/${encodeURIComponent(id)}`);
  },

  getThread(id: string): Promise<{ success: true; thread: GmailThread }> {
    return get(`/threads/${encodeURIComponent(id)}`);
  },

  modify(id: string, addLabelIds: string[] = [], removeLabelIds: string[] = []): Promise<{ success: true; message: GmailMessage }> {
    return post(`/messages/${encodeURIComponent(id)}/modify`, { addLabelIds, removeLabelIds });
  },

  async markRead(id: string): Promise<void> {
    // The server clears the Quantis alert for this message in the same call.
    await this.modify(id, [], ['UNREAD']);
    window.dispatchEvent(new CustomEvent('gmail:message-read', { detail: { messageId: id } }));
  },

  markUnread(id: string) {
    return this.modify(id, ['UNREAD'], []);
  },

  reconcileNotificationReads(): Promise<{ success: true; reconciled: number }> {
    return post('/reconcile-notification-reads');
  },

  attachmentUrl(msgId: string, attId: string, filename: string, mimeType: string): string {
    const params = new URLSearchParams({ filename, mimeType });
    return `${API_BASE}/attachments/${encodeURIComponent(msgId)}/${encodeURIComponent(attId)}?${params}`;
  },

  async fetchAttachment(msgId: string, attId: string, filename: string, mimeType: string): Promise<Blob> {
    const headers = await getAuthHeaders();
    const r = await fetch(this.attachmentUrl(msgId, attId, filename, mimeType), { headers });
    if (!r.ok) throw new Error(`Attachment fetch failed (${r.status})`);
    return await r.blob();
  },

  labels(): Promise<{ success: true; labels: GmailLabel[] }> {
    return get('/labels');
  },

  label(id: string): Promise<{ success: true; label: GmailLabel }> {
    return get(`/labels/${encodeURIComponent(id)}`);
  },

  profile(): Promise<{ success: true; profile: GmailProfile }> {
    return get('/profile');
  },

  startWatch(): Promise<{ success: true; expiration?: string; skipped?: 'already_watching' | 'pubsub_not_configured' }> {
    return post('/watch');
  },

  stopWatch(): Promise<{ success: true }> {
    return post('/watch/stop');
  },
};
